import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";

import { MarkdownContent } from "@/components/MarkdownContent";
import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { formatDateTimeCompact } from "@/utils/relative-time";

import { getFullName, getTargetLabel } from "../../adapter";
import { type TermsAndConditionsText } from "../../types";

interface ViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  record?: TermsAndConditionsText;
}

const SECTION_HEADING_CLASS = "font-semibold text-muted-foreground text-xs uppercase tracking-wide";

export const ViewModal = ({ isOpen, onClose, onEdit, record }: ViewModalProps) => {
  const { t } = useTranslation();
  const lastUpdated = formatDateTimeCompact(
    record?.last_update_datetime ?? record?.creation_datetime
  );
  const updatedBy = getFullName(record?.last_update_user ?? record?.creation_user);
  const version =
    record?.version === undefined || record?.version === null ? "" : String(record.version);

  return (
    <Dialog open={isOpen} onOpenChange={open => (open ? undefined : onClose())}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {t("termsAndConditionsText.view.title")}
            {record?.target ? (
              <Badge variant="secondary">{getTargetLabel(record.target, t)}</Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {[
              version && t("termsAndConditionsText.view.version", { version }),
              lastUpdated &&
                t("termsAndConditionsText.view.updatedBy", {
                  dateTime: lastUpdated,
                  fullName: updatedBy,
                }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex max-h-[65vh] flex-col gap-6 overflow-y-auto">
          <section className="flex flex-col gap-2">
            <h2 className={SECTION_HEADING_CLASS}>{t("termsAndConditionsText.list.shortText")}</h2>
            <MarkdownContent markdown={record?.short_text} />
          </section>
          <Separator />
          <section className="flex flex-col gap-2">
            <h2 className={SECTION_HEADING_CLASS}>{t("termsAndConditionsText.list.longText")}</h2>
            <MarkdownContent markdown={record?.long_text} />
          </section>
        </div>
        <DialogFooter>
          <WFPButton type="button" variant="outline" onClick={onClose}>
            {t("common.close")}
          </WFPButton>
          <WFPButton type="button" onClick={onEdit}>
            <Pencil className="size-4" aria-hidden="true" />
            {t("common.edit")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
